import { motion } from "framer-motion";
import {
    ArrowRight,
    ArrowUpRight,
    Activity,
    Package,
    GraduationCap,
    Heart,
    Rocket,
    Users,
    Globe,
    Code,
    TrendingUp,
    FileText,
    MessageSquare,
    ClipboardCheck,
    Handshake,
} from "lucide-react";
import { buttonVariants } from "../components/ui/button";

const fadeUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.6, ease: "easeOut" },
};

export default function CareersPage() {
    const careersUrl = "https://jobs.aiprof.com/careers/self";

    

    const domains = [
        {
            icon: <Activity className="w-6 h-6" />,
            title: "Healthcare",
            description:
                "AI-powered clinical documentation and decision support systems that save doctors hours every day while improving patient care quality and outcomes.",
            image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?q=80&w=1400&auto=format&fit=crop",
        },
        {
            icon: <Package className="w-6 h-6" />,
            title: "Warehouse",
            description:
                "Intelligent warehouse management systems that optimize inventory flow, reduce operational costs, and deliver real-time visibility across your entire supply chain.",
            image: "https://images.unsplash.com/photo-1553413077-190dd305871c?q=80&w=1400&auto=format&fit=crop",
        },
        {
            icon: <GraduationCap className="w-6 h-6" />,
            title: "Education",
            description:
                "AI-driven learning platforms that adapt to each student's pace and style, delivering personalized education at scale to transform how India learns.",
            image: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?q=80&w=1400&auto=format&fit=crop",
        },
    ];

    const values = [
        {
            index: "01",
            title: "Innovation first",
            description:
                "We build what others say can't be done. If it hasn't been solved yet, that's where we start. Breakthrough thinking isn't optional, it's the job.",
        },
        {
            index: "02",
            title: "Customer obsession",
            description:
                "Every product decision starts with the people who use it: doctors saving lives, warehouse teams moving goods, students chasing dreams. Their success is our scoreboard.",
        },
        {
            index: "03",
            title: "Excellence & rigor",
            description:
                "World-class isn't a target, it's the baseline. From code to design to customer experience, we ship work that sets the standard, not follows it.",
        },
        {
            index: "04",
            title: "Win together",
            description:
                "Diverse perspectives, one mission. We debate ideas fiercely, support each other unconditionally, and celebrate victories as a team.",
        },
    ];

    const perks = [
        {
            icon: <Rocket className="w-5 h-5" />,
            title: "Real impact, massive scale",
            description:
                "Your code runs in hospitals, warehouses, and classrooms. Build products that millions rely on every single day.",
        },
        {
            icon: <Code className="w-5 h-5" />,
            title: "Cutting-edge tech stack",
            description:
                "Work with Gen AI, LLMs, advanced ML pipelines, and modern infrastructure. No legacy systems, no technical debt.",
        },
        {
            icon: <TrendingUp className="w-5 h-5" />,
            title: "Rocket ship growth",
            description:
                "Join early, own outcomes, and grow with the company. Your career trajectory is only limited by your ambition and execution.",
        },
        {
            icon: <Users className="w-5 h-5" />,
            title: "Learn from the best",
            description:
                "Work alongside CMU alumni, Silicon Valley veterans, and founders who've built and exited billion-dollar companies.",
        },
        {
            icon: <Heart className="w-5 h-5" />,
            title: "Built for the long run",
            description:
                "Competitive compensation, comprehensive benefits, flexible schedules, and a culture that respects your life outside work.",
        },
        {
            icon: <Globe className="w-5 h-5" />,
            title: "Global standards, local impact",
            description:
                "Build world-class products from India that compete globally. International-quality engineering, design, and product culture.",
        },
    ];

    const gallery = [
        {
            src: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=1400&auto=format&fit=crop",
            alt: "Team working together",
            className: "col-span-2 row-span-2",
        },
        {
            src: "https://images.unsplash.com/photo-1531482615713-2afd69097998?q=80&w=1000&auto=format&fit=crop",
            alt: "Whiteboard discussion",
            className: "col-span-1 row-span-1",
        },
        {
            src: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?q=80&w=1000&auto=format&fit=crop",
            alt: "Pair collaboration",
            className: "col-span-1 row-span-1",
        },
        {
            src: "https://images.unsplash.com/photo-1543269865-cbf427effbad?q=80&w=1000&auto=format&fit=crop",
            alt: "Team conversation",
            className: "col-span-1 row-span-1",
        },
        {
            src: "https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=1000&auto=format&fit=crop",
            alt: "Planning session",
            className: "col-span-1 row-span-1",
        },
    ];

    const hiringSteps = [
        {
            icon: <FileText className="w-5 h-5" />,
            step: "01",
            title: "Apply",
            description:
                "Share your resume or portfolio. We review every application within five working days.",
        },
        {
            icon: <MessageSquare className="w-5 h-5" />,
            step: "02",
            title: "Intro chat",
            description:
                "Thirty minutes on your story, your ambitions, and what you'd love to build.",
        },
        {
            icon: <ClipboardCheck className="w-5 h-5" />,
            step: "03",
            title: "Skills round",
            description:
                "A practical, role-specific exercise. Real problems — no gotcha trivia.",
        },
        {
            icon: <Handshake className="w-5 h-5" />,
            step: "04",
            title: "Offer",
            description:
                "Meet the leadership, align on the role, and get your offer — usually within two weeks.",
        },
    ];

    return (
        <main className="pt-20 bg-white text-gray-900" data-testid="careers-page">
            {/* ============ Hero ============ */}
            <section className="relative overflow-hidden">
                <div className="max-w-6xl mx-auto px-4 sm:px-8 pt-16 sm:pt-20 pb-16 sm:pb-20">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: "easeOut" }}
                        className="max-w-4xl"
                    >
                        <p className="text-xs uppercase tracking-[0.35em] text-aiorange font-bold mb-6">
                            Careers at AI.Prof
                        </p>
                        <h1 className="font-display font-black text-5xl sm:text-7xl lg:text-8xl tracking-tight leading-[0.98] mb-8">
                            Build AI that runs
                            <br />
                            <span className="text-aiblue">the real world.</span>
                        </h1>
                        <p className="text-xl sm:text-2xl text-gray-500 leading-relaxed max-w-2xl mb-10">
                            We build intelligent products across healthcare, warehouse, and
                            education backed by world-class founders and held to a
                            world-class bar.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                            <a
                                href={careersUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={buttonVariants({
                                    className:
                                        "rounded-full bg-aiblue text-white hover:bg-aiblue/90 text-sm sm:text-base px-6 py-3 group",
                                })}
                            >
                                View Open Roles
                                <ArrowUpRight className="ml-2 h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                            </a>
                            <a
                                href="#domains"
                                className={buttonVariants({
                                    variant: "outline",
                                    className: "rounded-full text-sm sm:text-base px-6 py-3",
                                })}
                            >
                                What we build
                            </a>
                        </div>
                    </motion.div>
                </div>

                <div className="absolute -top-24 -right-24 w-[28rem] h-[28rem] bg-aiblue/5 rounded-full blur-3xl -z-10" />
                <div className="absolute bottom-0 left-1/3 w-80 h-80 bg-aiorange/5 rounded-full blur-3xl -z-10" />
            </section>

           

            {/* ============ Mission / manifesto ============ */}
            <section className="relative overflow-hidden bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50">
                <div className="max-w-5xl mx-auto px-4 sm:px-8 py-24 sm:py-32 relative z-10">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                        {/* Left side - Content */}
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <p className="text-xs uppercase tracking-[0.35em] text-aiorange font-bold mb-6">
                                Our mission
                            </p>
                            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl leading-tight mb-6 text-gray-900">
                                AI that works in the{" "}
                                <span className="text-aiblue">
                                    real world
                                </span>
                            </h2>
                            <div className="space-y-4 text-base sm:text-lg text-gray-700 leading-relaxed">
                                <p>
                                    Healthcare, warehouses, and education are the backbone of society.
                                    Yet they still run on paper, spreadsheets, and software built decades ago.
                                </p>
                                <p className="text-gray-900 font-medium">
                                    We're building AI products that solve real problems for real people,
                                    from doctors diagnosing patients to warehouse managers coordinating shipments
                                    to students learning new skills.
                                </p>
                            </div>
                        </motion.div>

                        {/* Right side - Card */}
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="relative"
                        >
                            <div className="bg-gradient-to-br from-orange-100 to-amber-100 rounded-3xl p-8 sm:p-10 shadow-xl relative overflow-hidden">
                                {/* Quote icon */}
                                <div className="text-6xl text-aiblue/20 font-serif mb-4">"</div>
                                
                                <p className="text-lg sm:text-xl text-gray-800 leading-relaxed mb-6 font-medium">
                                    Not demos. Not prototypes. Production systems that work at scale.
                                </p>
                                
                                <p className="text-base text-gray-700 leading-relaxed">
                                    Every line of code we write impacts real people in critical moments. That's the standard we hold ourselves to.
                                </p>

                                {/* Decorative element */}
                                <div className="absolute bottom-0 right-0 w-32 h-32 bg-aiblue/5 rounded-full blur-2xl" />
                            </div>
                        </motion.div>
                    </div>
                </div>
                
                {/* Decorative elements */}
                <div className="absolute top-20 left-0 w-96 h-96 bg-aiblue/10 rounded-full blur-3xl -z-0" />
                <div className="absolute bottom-20 right-0 w-96 h-96 bg-aiorange/10 rounded-full blur-3xl -z-0" />
            </section>

            {/* ============ Domains ============ */}
            <section id="domains" className="bg-gray-50/60 scroll-mt-24">
                <div className="max-w-6xl mx-auto px-4 sm:px-8 py-24 sm:py-32">
                    <motion.div {...fadeUp} className="max-w-2xl mb-14">
                        <p className="text-xs uppercase tracking-[0.35em] text-aiorange font-bold mb-4">
                            What we build
                        </p>
                        <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-tight mb-5">
                            One team. Three domains.
                        </h2>
                        <p className="text-lg text-gray-500 leading-relaxed">
                            We ship AI products where the stakes are real and joining us
                            means your work reaches all three.
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {domains.map((domain, i) => (
                            <motion.div
                                {...fadeUp}
                                transition={{ ...fadeUp.transition, delay: i * 0.1 }}
                                key={domain.title}
                                className="group bg-white rounded-3xl overflow-hidden border border-gray-100 hover:border-aiblue/30 hover:shadow-xl transition-all duration-300"
                            >
                                <div className="relative h-48 overflow-hidden">
                                    <img
                                        src={domain.image}
                                        alt={domain.title}
                                        loading="lazy"
                                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                </div>
                                <div className="p-7">
                                    <div className="w-11 h-11 rounded-xl bg-aiblue/10 text-aiblue flex items-center justify-center mb-5">
                                        {domain.icon}
                                    </div>
                                    <h3 className="font-display font-bold text-2xl mb-3">
                                        {domain.title}
                                    </h3>
                                    <p className="text-gray-500 text-[15px] leading-relaxed">
                                        {domain.description}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ Values ============ */}
            <section className="max-w-6xl mx-auto px-4 sm:px-8 py-24 sm:py-32">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-4">
                        <motion.div {...fadeUp} className="lg:sticky lg:top-32">
                            <p className="text-xs uppercase tracking-[0.35em] text-aiorange font-bold mb-4">
                                How we work
                            </p>
                            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-tight">
                                Four values.
                                <br />
                                Zero exceptions.
                            </h2>
                        </motion.div>
                    </div>

                    <div className="lg:col-span-8">
                        {values.map((value, i) => (
                            <motion.div
                                {...fadeUp}
                                transition={{ ...fadeUp.transition, delay: i * 0.05 }}
                                key={value.index}
                                className="group flex gap-6 sm:gap-10 py-10 first:pt-0"
                            >
                                <span className="font-display font-black text-sm text-gray-300 pt-1.5 group-hover:text-aiorange transition-colors">
                                    {value.index}
                                </span>
                                <div>
                                    <h3 className="font-display font-bold text-2xl mb-3">
                                        {value.title}
                                    </h3>
                                    <p className="text-gray-500 leading-relaxed max-w-xl">
                                        {value.description}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ Life at AI.Prof (photo collage) ============ */}
            <section className="bg-gray-50/60">
                <div className="max-w-6xl mx-auto px-4 sm:px-8 py-24 sm:py-32">
                    <motion.div {...fadeUp} className="max-w-2xl mb-14">
                        <p className="text-xs uppercase tracking-[0.35em] text-aiorange font-bold mb-4">
                            Life at AI.Prof
                        </p>
                        <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-tight mb-5">
                            Serious about the mission, not about ourselves
                        </h2>
                        <p className="text-lg text-gray-500 leading-relaxed">
                            Deep-work mornings, loud demo Fridays, and a team that debates
                            product decisions as fiercely as system design.
                        </p>
                    </motion.div>

                    <motion.div
                        {...fadeUp}
                        className="grid grid-cols-2 md:grid-cols-4 auto-rows-[10rem] sm:auto-rows-[12rem] gap-3 sm:gap-4"
                    >
                        {gallery.map((photo) => (
                            <div
                                key={photo.src}
                                className={`relative rounded-2xl overflow-hidden ${photo.className}`}
                            >
                                <img
                                    src={photo.src}
                                    alt={photo.alt}
                                    loading="lazy"
                                    className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                                />
                            </div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* ============ Perks ============ */}
            <section className="max-w-6xl mx-auto px-4 sm:px-8 py-24 sm:py-32">
                <motion.div {...fadeUp} className="max-w-2xl mb-14">
                    <p className="text-xs uppercase tracking-[0.35em] text-aiorange font-bold mb-4">
                        What you get
                    </p>
                    <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-tight">
                        The upside of a rocket ship, without the burnout
                    </h2>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
                    {perks.map((perk, i) => (
                        <motion.div
                            {...fadeUp}
                            transition={{ ...fadeUp.transition, delay: i * 0.05 }}
                            key={perk.title}
                        >
                            <div className="w-11 h-11 rounded-xl bg-aiblue/10 text-aiblue flex items-center justify-center mb-5">
                                {perk.icon}
                            </div>
                            <h3 className="font-display font-bold text-xl mb-2">
                                {perk.title}
                            </h3>
                            <p className="text-gray-500 text-[15px] leading-relaxed">
                                {perk.description}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </section>


            {/* ============ Final CTA ============ */}
            <section className="relative overflow-hidden bg-gray-950">
                <div className="max-w-5xl mx-auto px-4 sm:px-8 py-16 sm:py-20 relative z-10">
                    <motion.div {...fadeUp} className="max-w-3xl mx-auto text-center">
                        <p className="text-xs uppercase tracking-[0.35em] text-aiorange font-bold mb-5">
                            Join us
                        </p>
                        <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-tight mb-5">
                            Do the best work{" "}
                            <span className="bg-gradient-to-r from-aiblue via-purple-400 to-aiorange bg-clip-text text-transparent">
                                of your life.
                            </span>
                        </h2>
                        <p className="text-base sm:text-lg text-gray-400 leading-relaxed max-w-2xl mx-auto mb-8">
                            Every open role across healthcare, warehouse, and education with
                            details, requirements, and a two-minute application lives on our
                            careers portal.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-5 sm:items-center justify-center">
                            <a
                                href={careersUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-gray-100 font-bold text-sm sm:text-base px-6 py-3 rounded-full shadow-2xl hover:shadow-white/20 transition-all duration-300 hover:scale-[1.03] group"
                            >
                                View Open Roles
                                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                            </a>
                          
                        </div>
                    </motion.div>
                </div>

                <div className="absolute -top-20 right-0 w-96 h-96 bg-aiblue/15 rounded-full blur-3xl" />
                <div className="absolute -bottom-24 left-1/4 w-80 h-80 bg-aiorange/10 rounded-full blur-3xl" />
            </section>
        </main>
    );
}
